import { printLine } from './terminal.js';
import { API_METHODS, CONTENT_TYPES, CSS_CLASSES, CONFIG, MESSAGES } from './constants.js';

/**
 * Resolves the API base URL at module load time.
 * Returns an empty string in production so that all fetch calls use relative paths
 * (CloudFront routes `/api/*` to App Runner automatically).
 *
 * @returns {string}
 */
export const getApiBaseUrl = () => {
    if (typeof window === 'undefined' || !window.location) return CONFIG.EMPTY;

    const { hostname, protocol, port } = window.location;
    const isLocal = hostname === CONFIG.LOCALHOST ||
        hostname === CONFIG.LOOPBACK ||
        hostname === '0.0.0.0' ||
        hostname.startsWith('192.168.') ||
        hostname.startsWith('172.') ||
        hostname.startsWith('10.') ||
        protocol === CONFIG.FILE_PROTOCOL;

    if (isLocal && port !== CONFIG.DEFAULT_PORT) {
        return CONFIG.LOCAL_API_BASE;
    }
    return CONFIG.EMPTY; // Relative path for production (under CloudFront router)
};

export const API_BASE = getApiBaseUrl();

/**
 * Fetches a REST endpoint and invokes {@code callback} with the parsed JSON body.
 *
 * @param {string}   endpoint  - Path starting with `/api/…`
 * @param {Function} callback  - Called with the parsed response data on success
 * @param {boolean}  [isPost]  - When true, sends a POST with JSON body
 * @param {*}        [bodyData] - Data to JSON-serialize as the request body
 * @returns {Promise<*>}
 */
export async function fetchApi(endpoint, callback, isPost = false, bodyData = null) {
    printLine(MESSAGES.API_FETCHING, CSS_CLASSES.COMMENT);

    const url = `${API_BASE}${endpoint}`;
    const options = {
        method: isPost ? API_METHODS.POST : API_METHODS.GET,
        headers: { 'Content-Type': CONTENT_TYPES.JSON },
        ...(isPost && bodyData ? { body: JSON.stringify(bodyData) } : {}),
    };

    try {
        const response = await fetch(url, options);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        callback(data, response);
        return data;
    } catch (err) {
        printLine(MESSAGES.API_CONNECTION_ERROR(err.message), CSS_CLASSES.ERROR);
        printLine(MESSAGES.API_PORT_WARNING, CSS_CLASSES.COMMENT);
        throw err;
    }
}
